import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  ConnectionTransport,
  ConnectionTransports,
  SignalR,
  SignalRConfiguration,
  SignalRConnection
} from '@henkkelder/ng2-signalr';
import { Store } from '@ngrx/store';
import {
  ENV_CONFIG,
  ElementTypes,
  EnvironmentConfig,
  NFIdentity,
  SignalRConnectionStatus,
  TagCategory,
  UOM
} from '@nibras/core/common';
import { BehaviorSubject, EMPTY, Observable, firstValueFrom, from, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth/auth.service';
import { CacheService } from './cache.service';
import { CommonService } from './common.service';
import { DIALOG_SERVICE, IDialogService } from './dialog.interface';
import { Caller, HttpService } from './http.service';
import { LocaleService } from './locale.service';
import { AppConstants } from './model/constants.model';
import {
  PortConnections,
  PortTemplates,
  dcAttributeTemplate,
  dcConnectedAppsFilter
} from './model/dcAttributeTemplate.model';
import { dcElement } from './model/dcElement.model';
import { dcElementTemplate } from './model/dcElementTemplate.model';
import { dcElementTemplateHeader } from './model/dcElementTemplateHeader.model';
import { dcHierarchyTVP } from './model/dcHierarchyTVP';
import { dcKPI } from './model/dcKPIs.model';
import { NavigationTreeSearch, dcNavigationTree } from './model/dcNavigationtree.model';
import { TagFilter, dcSearchTags } from './model/dcSearchTags.model';
import { dcTag } from './model/dcTag.model';
import { dcTreeNode } from './model/dcTreeNode.model';
import { dcUOM } from './model/dcUOM.model';
import { dcValueClassType } from './model/dcValueClassType.model';
import { dcAttributeFilterTVP } from './model/dcattribute-filter-tvp.model';
import { dcAttribute, dcAttributeHeader, dcSubstitutionParameter } from './model/dcattribute.model';
import { dcDataReference } from './model/dcdataReference.model';
import { dcElementHeader } from './model/dcelementheader.model';
import {
  dcParameterizationOutput,
  dcQueryParameterHeaderExtn
} from './model/dcqueryParameter.model';
import { Dictionary } from './model/dictionary';
import { FileAttachmentDetail } from './model/file-attachment-detail.model';
import { FileSaveInfo } from './model/file-save-info.model';
import { GenericValue } from './model/generic-value.model';
import { KeyValuePair } from './model/key-value-pair';
import { LoggingDetails } from './model/log-settings.model';
import { NotificationMessage, SDKNotifyLog } from './model/notification-message.model';
import { NxGroup } from './model/nx-group.model';
import { DataRequest } from './model/request/request.model';
import { DataMethods } from './model/response/data-method.enum';
import {
  dcActionInfo,
  dcActionInfoBasic,
  dcActionInfoSingle
} from './model/response/dcActionInfo.model';
import { SmartDataRequestParam } from './model/smart-data-request-param.model';
import { Feedback, StatisticsTVP } from './model/statisticsTVP.model';
import { dcPortTemplateValue, dcSuggestedValue } from './model/suggested-value.model';
import { TimeRange } from './model/time-range.model';
import { Role, User, UserSetting } from './model/user.model';
import { ServiceInfo } from './model/version.model';
import { ServiceBaseService } from './service-base.service';
import { SharedCommonService } from './shared-common.service';
import { ISnackbarService, SNACKBAR_SERVICE } from './snackbar.interface';
import { setSignalRConnectionAction, userDetailsResultAction } from './store/core-store.actions';
import { IToastService, TOAST_SERVICE } from './toast.interface';

const CACHE_SIZE = 1;

@Injectable()
export class CoreService extends ServiceBaseService {
  constructor(
    private http: HttpClient,
    private httpService: HttpService,
    private route: Router,
    private _signalR: SignalR,
    private authService: AuthService,
    private commonService: CommonService,
    private store: Store,
    private cacheService: CacheService,
    @Inject(TOAST_SERVICE) private toastService: IToastService,
    @Inject(DIALOG_SERVICE) private dialogService: IDialogService,
    @Inject(SNACKBAR_SERVICE) private snackbarService: ISnackbarService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(ENV_CONFIG) private environmentConfig: EnvironmentConfig
  ) {
    super();

    this.setWindowFunctions();
  }

  public signalRConnectionStatus: SignalRConnectionStatus;
  public signalRConnection: SignalRConnection;
  public ejsTooltipLastId = 0;

  eventNotifications = new BehaviorSubject<NotificationMessage<SDKNotifyLog>>(null);

  eventContextTime: Date;

  private elementTemplateCache: Map<number, dcElementTemplateHeader[]> = new Map();
  private uomCache: Observable<dcUOM[]>;
  private readonly GET_ALL_TEMPLATE_BY_ELEMENT_TYPE_CACHE_KEY = 'getAllTemplatesByElementType_';
  private readonly GET_ALL_ELEMENT_TYPE_CACHE_KEY = 'getAllElementType';

  getUserAndConnectSignalR() {
    const callback = this.authService.getUser().pipe(
      switchMap(userDetails => {
        if (userDetails == null) {
          this.route.navigate(['/login']);
          return;
        }

        if (userDetails.Setting) {
          LocaleService.userSettings = userDetails.Setting;
        } else {
          LocaleService.userSettings = {
            DateFormat: AppConstants.DATE_FORMAT,
            DateTimeFormat: AppConstants.DATE_TIME_FORMAT
          } as UserSetting;
        }

        return this.connectSignalR(userDetails);
      })
    );

    return callback;
  }

  addEditUserSave(data: User): Observable<User> {
    return this.httpService.post<User>('User/Save', data);
  }

  getUserDetails(loginID: string): Observable<User> {
    return this.httpService.get<User>(`User/${loginID}`);
  }

  getRoleDetails(roleID: number): Observable<Role> {
    return this.httpService.get<Role>(`Role/${roleID}`);
  }

  addEditRoleSave(data: Role): Observable<Role> {
    return this.httpService.post<Role>('Role/Save', data);
  }

  public connectSignalR(userDetails: User) {
    const speedTestExpired = this.hasSpeedTestExpired();
    const forceTransport = localStorage.getItem(AppConstants.FORCE_TRANSPORT_TYPE);
    let transport: ConnectionTransport;
    if (forceTransport) {
      transport = new ConnectionTransport(forceTransport);
    }

    return this._connectSignalR(userDetails.UserID, transport).pipe(
      map(connection => {
        if (!transport && speedTestExpired && userDetails?.Setting?.SpeedThreshold) {
          this.checkSpeedAndReload(userDetails.Setting.SpeedThreshold);
        }

        this.store.dispatch(userDetailsResultAction(userDetails));
        return connection;
      })
    );
  }

  private hasSpeedTestExpired() {
    const speedTestExpiryString = localStorage.getItem(AppConstants.LAST_SPEED_TESTED_ON);
    if (speedTestExpiryString && speedTestExpiryString) {
      const speedTestExpiry = SharedCommonService.toDateTime(speedTestExpiryString);
      const currentDate = new Date();
      if (speedTestExpiry.getTime() > currentDate.getTime()) {
        return false;
      }
    }

    return true;
  }

  private setWindowFunctions() {
    // const windowLocal = this.document.defaultView as any;
    // windowLocal.speedTestService = this.speedTestService;
    // windowLocal.speedTest = (fileSize?: string) => {
    //   windowLocal.speedTestService.startTest(fileSize).subscribe();
    // };
    // windowLocal.connection = (transport: string) => {
    //   localStorage.setItem(AppConstants.FORCE_TRANSPORT_TYPE, transport);
    //   this.document.location.reload();
    // };
    // windowLocal.switchToSlow = () => windowLocal.connection(ConnectionTransports.longPolling);
    // windowLocal.clearConnection = () => localStorage.removeItem(AppConstants.FORCE_TRANSPORT_TYPE);
  }

  private _connectSignalR(userID: number, connectionTransport?: ConnectionTransport) {
    const config = this.createConfig(userID);
    if (connectionTransport) {
      config.transport = [connectionTransport];
    }

    const promise = this._signalR.connect(config);
    const observable = from(promise);
    return observable.pipe(
      map((connection: SignalRConnection) => {
        this.signalRConnectionStatus = SignalRConnectionStatus.connected;
        this.signalRConnection = connection;
        this.store.dispatch(setSignalRConnectionAction(connection));
        this.authService.userSessionID = connection.id;
        console.log(
          '%c SignalR Connected',
          // eslint-disable-next-line max-len
          'font-weight: bold; font-size: 28px;color: #9bf13f; text-shadow: 1px 1px 0 black, 1px -1px 0px black,-1px 1px 0px black, -1px -1px 0px black;'
        );
        return connection;
      })
    );
  }

  public checkSpeedAndReload(speedThreshold: number) {
    const FORCE_TRANSPORT_TYPE = localStorage.getItem(AppConstants.FORCE_TRANSPORT_TYPE);
    if (!FORCE_TRANSPORT_TYPE) {
      this.showConfirmToSwitchToSlowNetwork().subscribe();
    }

    // TODO network-information-types can be used for strong types of navigator
    console.log('Network speed ', (window as any).navigator.connection);

    // setTimeout(() => {
    //   try {
    //     this.speedTestService.startTest().subscribe(speed => {
    //       if (speed < speedThreshold) {
    //         this.showConfirmToSwitchToSlowNetWork().subscribe();
    //       } else {
    //         this.switchToNormalNetwork();
    //         this.setLastSpeedTestedOn();
    //       }
    //     });
    //   } catch (ex) {
    //     console.error(ex);
    //   }
    // });
  }

  public isOnSlowNetwork() {
    return !!localStorage.getItem(AppConstants.FORCE_TRANSPORT_TYPE);
  }

  public showConfirmToSwitchToSlowNetwork(fromUI = false) {
    return this.dialogService
      .showConfirm({
        message: fromUI
          ? 'We need to reload NibrasNX to Switch to lite network'
          : `Your network seems to be slow, we need to reload NibrasNX to use a relaible connection.`,
        title: fromUI ? 'Switch to lite network' : 'Slow network - Unable to load data',
        cancelButton: 'Cancel',
        acceptButton: 'Reload',
        disableClose: true
      })
      .pipe(
        map((accept: boolean) => {
          if (accept) {
            this.switchToSlowNetwork(true);
          } else {
            this.switchToNormalNetwork();
          }
          return accept;
        })
      );
  }

  public switchToNormalNetwork(forceReload = false) {
    localStorage.removeItem(AppConstants.LAST_SPEED_TESTED_ON);
    localStorage.removeItem(AppConstants.FORCE_TRANSPORT_TYPE);

    if (forceReload) {
      this.document.location.reload();
    }
  }

  public switchToSlowNetwork(forceReload = false) {
    this.setLastSpeedTestedOn();
    localStorage.setItem(
      AppConstants.FORCE_TRANSPORT_TYPE,
      ConnectionTransports.longPolling.toString()
    );

    if (forceReload) {
      this.document.location.reload();
    }
  }

  private setLastSpeedTestedOn() {
    const dayOffset = AppConstants.SPEED_TESTED_INTERVAL_DAYS;
    const millisecondOffset = dayOffset * 24 * 60 * 60 * 1000;
    const today = new Date();
    today.setTime(today.getTime() + millisecondOffset);
    localStorage.setItem(AppConstants.LAST_SPEED_TESTED_ON, today.toISOString());
  }

  createConfig(userID: number): SignalRConfiguration {
    const config = new SignalRConfiguration();
    config.hubName = 'ServerPushBroadCastHub';
    config.qs = { user: userID };
    //config.qs = {  };
    config.url = this.environmentConfig.apiBaseUrl;
    config.logging = true;
    config.executeStatusChangeInZone = false;
    config.transport = [
      ConnectionTransports.webSockets,
      ConnectionTransports.serverSentEvents,
      ConnectionTransports.foreverFrame,
      ConnectionTransports.longPolling
    ];
    return config;
  }

  reconnectSingalRIfNotConnected(connectionTransport?: ConnectionTransport) {
    if (this.signalRConnectionStatus == SignalRConnectionStatus.disconnected) {
      return this._connectSignalR(this.authService.userElementVersionId, connectionTransport);
    } else {
      return of(this.signalRConnection);
    }
  }

  // public updateDefaultRole(usersetting: UserSetting) {
  //   const req = this.httpService.post('User/SetDefaultRole', usersetting).subscribe(
  //     res => {
  //       console.log(res);
  //     },
  //     err => {
  //       console.log('Error occured');
  //     }
  //   );
  // }

  public getSingletone(obj: dcTreeNode, supressErrorCodes?: number[]): Observable<dcElement> {
    return this.httpService.post<dcElement>(
      `Element/GetSingletonElement`,
      obj,
      null,
      null,
      supressErrorCodes
    );
  }

  // TODO some components read non-existing fields of the result
  public createElements(elements: dcElement[]): Observable<any> {
    return this.httpService.post<dcActionInfo<any>>('tags/saveElements', elements);
  }

  public writeVisitStatistics(statistics: StatisticsTVP): Observable<dcActionInfo<any>> {
    //return this.httpService.post<dcActionInfo<any>>('App/WriteVisitStatistics', statistics);
    return null;
  }

  getAllTemplates(isAbstract?: boolean): Observable<dcElementTemplateHeader[]> {
    return this.httpService.get<dcElementTemplateHeader[]>(
      `tags/GetAllTemplates?isAbstract=${isAbstract}`
    );
  }

  public getAllTemplatesByID(
    elementversionid: number,
    elementtype: number
  ): Observable<dcElementTemplateHeader[]> {
    return this.httpService.get<dcElementTemplateHeader[]>(
      `tags/GetAllTemplatesByID?elementversionid=${elementversionid}&elementtype=${elementtype}`
    );
  }

  public getAllTemplatesByElementType(
    elementType: NFIdentity
  ): Observable<dcElementTemplateHeader[]> {
    return this.cacheService.get(
      this.getTemplatesByElementTypeCacheKey(elementType),
      this.httpService.get<dcElementTemplateHeader[]>(
        `ElementTemplate/GetAllTemplatesByType/${elementType}`
      ),
      8 * 60 * 60 * 1000 // 8 hours
    );
  }

  private getTemplatesByElementTypeCacheKey(elementType: NFIdentity): string {
    return this.GET_ALL_TEMPLATE_BY_ELEMENT_TYPE_CACHE_KEY + elementType;
  }

  public updateTemplateCache(newTemplate: dcElementTemplateHeader) {
    if (!newTemplate) {
      console.error('Expected newTemplate missing for updateTempalteCache');
      return;
    }

    this.cacheService.appendData(
      this.getTemplatesByElementTypeCacheKey(newTemplate.ElementTypeID),
      newTemplate
    );
  }

  getAllElementType(fillEnum = false): Observable<dcSuggestedValue[]> {
    const apiCall = this.httpService.get<dcSuggestedValue[]>('tags/GetAllElementType').pipe(
      tap(elementTypes => {
        if (!fillEnum) return;

        const ElementTypesObj = ElementTypes as any as Dictionary<string | number>;
        elementTypes.forEach(elementType => {
          if (elementType.ValueId && !ElementTypesObj[+elementType.ValueId]) {
            ElementTypesObj[+elementType.ValueId] = elementType.ValueString?.replace(
              'Nibras.Core.NF.',
              ''
            );
            ElementTypesObj[elementType.ValueString] = +elementType.ValueId;
          }
        });

        return elementTypes;
      })
    );

    return this.cacheService
      .get(
        this.GET_ALL_ELEMENT_TYPE_CACHE_KEY,
        apiCall,
        8 * 60 * 60 * 1000 // 8 hours
      )
      .pipe(catchError(this._errorHandler));
  }

  clearGetAllElementTypeCache() {
    this.cacheService.clearCache(this.GET_ALL_ELEMENT_TYPE_CACHE_KEY);
  }

  public getElementDetails(
    rootElementVersionID: number,
    elementPath: string,
    isAttributesValuesRequired: boolean = true,
    cancelToken?: string
  ): Observable<dcElement> {
    const dcAttributeFilter = {
      RootElementVersionID: rootElementVersionID,
      ElementPath: elementPath
    } as dcAttributeFilterTVP;
    return this.httpService.post<dcElement>(
      `tags/GetElementDetails/${isAttributesValuesRequired}`,
      dcAttributeFilter,
      cancelToken
    );
  }

  public getRootElementDetailsByTag(
    customTags: dcTag[],
    cancelToken?: Caller
  ): Observable<dcElement> {
    return this.getRootElementTagInfo(customTags, cancelToken).pipe(
      switchMap(rootTagInfo => {
        if (rootTagInfo && rootTagInfo.length > 0) {
          const tagDetails = rootTagInfo[0];
          return this.getElementDetails(tagDetails.ObjectID, '.\\FILES', true, cancelToken);
        }
        return EMPTY;
      })
    );
  }

  public getPortConnectionsDetails(
    RootElementVersionId: number,
    ElementVersionID: number,
    cancelToken: Caller
  ): Observable<PortTemplates[]> {
    return this.httpService.get<PortTemplates[]>(
      `Element/Ports?RootElementVersionId=${RootElementVersionId}&ElementVersionID=${ElementVersionID}`,
      null,
      cancelToken
    );
  }

  public getElementDetailsbyAttributeFilter(
    attributeFilter: dcAttributeFilterTVP
  ): Observable<dcElement> {
    return this.httpService.post<dcElement>('tags/GetElementDetails', attributeFilter);
  }

  // public getElementDetailsByTagId(tag: dcTag): Observable<dcElement> {
  //   return this.httpService.post<dcElement>(`Element/GetElementDetails`, tag);
  // }

  public getElementDetailsByType(
    RootElementVersionID: number,
    ElementTypeID: number,
    excludeValueTypeId = 0
  ): Observable<dcElement[]> {
    return this.httpService.get<dcElement[]>(
      // eslint-disable-next-line max-len
      `Element/GetElementDetailsByType?RootElementVersionID=${RootElementVersionID}&ElementTypeID=${ElementTypeID}&excludeValueTypeId=${excludeValueTypeId}`
    );
  }

  public GetElementAttributesByAttributeFilter(
    elementVersionID: number,
    elementPath: string,
    attributeTemplateVersionID: number,
    attributePath: string
  ): Observable<dcAttribute[]> {
    const dcAttributeFilter = [
      {
        ElementVersionID: elementVersionID,
        ElementPath: elementPath,
        AttributeTemplateVersionID: attributeTemplateVersionID,
        AttributeTemplatePath: attributePath
      }
    ] as dcAttributeFilterTVP[];
    return this.httpService.post<dcAttribute[]>(
      'Element/GetElementAttributesByAttributeFilter',
      dcAttributeFilter
    );
  }

  public updateAttributes(attributes: dcAttribute): Observable<any> {
    return this.httpService.post<dcActionInfo<any>[]>('tags/UpdateAttributes', attributes);
  }

  /**
   * @deprecated use the endpoint from ElementTemplate controller which loads only the element template
   * and its attribute headers without DataReference
   */
  public getElementTemplateOld(templateTag: dcTag, fetchBaseTemplateAttributes: boolean = true) {
    return this.httpService.post<dcElementTemplate[]>(
      `tags/GetElementTemplate?fetchBaseTemplateAttributesFlag=${fetchBaseTemplateAttributes}`,
      templateTag
    );
  }

  public getElementTemplate(templateTag: dcTag, fetchBaseTemplateAttributes: boolean = true) {
    return this.httpService.post<dcElementTemplate[]>(
      `elementtemplate/GetElementTemplate?fetchBaseTemplateAttributesFlag=${fetchBaseTemplateAttributes}`,
      templateTag
    );
  }

  public getDataFunctionTemplates(dataMethod: DataMethods) {
    return this.cacheService.get(
      dataMethod.toString(),
      this.httpService.get<dcElementTemplate>(
        `ElementTemplate/GetDataFunctionTemplate/${dataMethod}`
      ),
      8 * 60 * 60 * 1000, // 8 hours
      true
    );
  }
  /**
   * @deprecated use the endpoint from ElementTemplate controller which loads only the element template
   * and its attribute headers without DataReference
   */
  public GetElementTemplateWithParentsOld(
    templateTag: dcTag,
    fetchParents = false,
    fetchPorts = false,
    fetchBaseTemplateAttributes = true,
    isTagsRequired = false,
    cancelToken?: string
  ) {
    return this.httpService.post<dcElementTemplate[]>(
      // eslint-disable-next-line max-len
      `tags/GetElementTemplateWithParents?fetchParents=${fetchParents}&fetchPorts=${fetchPorts}&fetchBaseTemplateAttributes=${fetchBaseTemplateAttributes}&IsTagsRequired=${isTagsRequired}`,
      templateTag,
      cancelToken || this.cacheService.generateCacheKeyForTag(templateTag)
    );
  }

  public saveElementTemplates(elementTemplates: dcElementTemplate[]): Observable<any> {
    return this.httpService.post<dcActionInfo<any>[]>(
      'tags/saveElementTemplates',
      elementTemplates
    );
  }

  public GetElementTemplateWithParents(
    templateTag: dcTag,
    fetchParents = false,
    fetchPorts = false,
    fetchBaseTemplateAttributes = true,
    isTagsRequired = false,
    cancelToken?: string
  ) {
    return this.httpService.post<dcElementTemplate[]>(
      // eslint-disable-next-line max-len
      `elementtemplate/GetElementTemplateWithParents?fetchParents=${fetchParents}&fetchPorts=${fetchPorts}&fetchBaseTemplateAttributes=${fetchBaseTemplateAttributes}&IsTagsRequired=${isTagsRequired}`,
      templateTag,
      cancelToken || this.cacheService.generateCacheKeyForTag(templateTag)
    );
  }

  public updateAttributeTemplates(attributes: dcAttributeTemplate) {
    console.log(attributes);
    this.httpService.post('tags/updateAttributeTemplates', attributes).subscribe({
      next: res => {
        console.log(res);
        this.snackbarService.openInfoBar('Saved Successfully');
      },
      error: () => {
        console.log('Error occured');
        this.snackbarService.openInfoBar('Error occured');
      }
    });
  }

  // Not using, can be removed
  public getAttributeTemplates(tag: dcTag) {
    return this.httpService.post<dcTreeNode[]>('tags/GetAttributeTemplates', tag);
  }

  // public getAttributeTemplatesByRule(tag: dcTag) {
  //   return this.httpService.post<dcAttributeTemplate[]>('tags/GetAttributeTemplatesByRule', tag);
  // }

  public getAttributeTemplatesByRule(tag: dcTag) {
    return this.httpService.post<any>('tags/GetAttributeTemplatesByRule', tag);
  }

  public GetElementTemplatesByRule(tag: dcTag) {
    if (tag) {
      const tempTag = new dcTag();
      tempTag.ObjectID = tag.ObjectID;
      tempTag.SourceObjectID = tag.SourceObjectID;
      tempTag.ElementTypeID = tag.ElementTypeID;
      tempTag.TagName = tag.TagName;
      tempTag.IsLazyLoading = false;
      tempTag.IsShallowsearch = false;
      return this.httpService.post<{
        m_Item1: dcElementTemplate[];
        m_Item2: dcTreeNode[];
      }>('tags/GetElementTemplatesByRule', tempTag);
    }
  }

  public getAttributes(tag: dcTag) {
    return this.httpService.post<dcAttribute[]>('tags/GetAttributes', tag);
  }

  public getElementAttributes(elementId: number, includeDataReference = false) {
    return this.httpService.get<dcAttribute[]>(
      'Element/GetAttributes/' + elementId + '/' + includeDataReference
    );
  }

  public getAttributeValueByTagParameter(trendDataRequest: DataRequest) {
    return this.httpService.post<any>('tags/  ', trendDataRequest);
  }

  public GetAttributeTemplatesAndQueryData(
    ElementTemplateVersionID: number
  ): Observable<dcAttributeTemplate[]> {
    return this.httpService.get(
      `tags/GetAttributeTemplatesAndQueryData?ElementTemplateVersionID=${ElementTemplateVersionID}`
    );
  }

  public GetAttributeTemplates(ElementTemplateVersionID: number) {
    return this.httpService.post<dcAttributeTemplate[]>(
      'tags/GetAttributeTemplates?ElementTemplateVersionID=' + ElementTemplateVersionID,
      null
    );
  }

  getDatareferencesByAttributeId(
    AttributeId: number,
    template: boolean,
    elementVersionId: number
  ): Observable<dcDataReference> {
    return (
      this.httpService
        // .get<dcDataReference>('tags/GetDatareferencesByAttributeId',AttributeId)
        .get<dcDataReference>(
          `tags/GetDatareferencesByAttributeId?AttributeId=${AttributeId}&template=${template}&elementversionid=${elementVersionId}`
        )
    );
    // .catch(this._errorHandler);
  }

  // GetAllValueClassTypes(): Observable<dcValueClassType[]> {
  //   return this.httpService
  //     .get<dcValueClassType[]>('tags/GetAllValueClassTypes')
  //     .catch(this._errorHandler);
  // }

  GetAllValueClassTypes(isPrimitive: boolean): Observable<dcValueClassType[]> {
    return this.cacheService
      .get(
        'AllValueClassTypes',
        this.httpService.get<dcValueClassType[]>(
          `tags/GetAllValueClassTypes?isPrimitive=${isPrimitive}`
        ),
        8 * 60 * 60 * 1000 // 8 hours
      )
      .pipe(catchError(this._errorHandler));
  }

  getTag(valueTypeId: number, searchText = '') {
    //Todo
    const tag = new dcTag();
    tag.ObjectID = valueTypeId;
    switch (valueTypeId) {
      case NFIdentity.ElementTemplate:
        tag.CategoryID = TagCategory.ElementTemplateSelfTag;
        break;
      case NFIdentity.Element:
        tag.CategoryID = TagCategory.ElementSelfTag;
        break;
    }
    if (searchText != undefined && searchText.length > 0) {
      tag.TagName = searchText;
    }
    return tag;
  }

  public getSuggestedCacheValues(
    tag: dcTag,
    filterIDs: number[] = null,
    params: Dictionary<GenericValue> = null,
    forceReload = false
  ): Observable<dcSuggestedValue[]> {
    const { key, tagFilter } = this.getSuggestedValuesKeyByTagFilter(tag, filterIDs, params);
    return this.cacheService.get(
      key,
      this.httpService.post<dcSuggestedValue[]>(`tags/GetSuggestedValues`, tagFilter),
      720 * 60 * 60 * 1000, // 30 days
      false,
      forceReload
    );
  }

  public getSuggestedValuesKeyByTagFilter(
    tag: dcTag,
    filterIDs: number[],
    params: Dictionary<GenericValue>
  ) {
    const tagFilter = new TagFilter(tag, filterIDs, params);
    const key = this.getSuggestedValuekey(tagFilter);
    return { key, tagFilter };
  }

  GetSuggestedValues(
    valueTypeId: number,
    searchText: string = '',
    filterIDs: number[] = null,
    params: Dictionary<GenericValue> = null,
    forceReload = false
  ) {
    const tag = this.createTagForSuggestedValues(valueTypeId, searchText);

    return this.getSuggestedCacheValues(tag, filterIDs, params, forceReload);
  }

  public createTagForSuggestedValues(valueTypeId: number, searchText: string) {
    const tag = new dcTag();
    tag.ObjectID = valueTypeId;
    switch (valueTypeId) {
      case NFIdentity.ElementTemplate:
        tag.CategoryID = TagCategory.ElementTemplateSelfTag;
        break;
      case NFIdentity.Element:
        tag.CategoryID = TagCategory.ElementSelfTag;
        break;
    }
    if (searchText != undefined && searchText.length > 0) {
      tag.TagName = searchText;
    }
    return tag;
  }

  getSuggestedValuekey(tagFilter: TagFilter) {
    return tagFilter.Tag
      ? tagFilter.Tag.ObjectID + '_' + tagFilter.Tag.CategoryID + '_' + tagFilter.Tag.TagName
      : tagFilter.MethodParams
      ? tagFilter.MethodParams.toString()
      : null;
  }

  getSuggestedValueKeyByTag(tag: dcTag) {
    return this.getSuggestedValuekey(new TagFilter(tag));
  }

  public saveElements(elements: dcElement[]): Observable<dcActionInfoBasic> {
    return this.httpService.post<dcActionInfoBasic>('tags/saveElements', elements);
  }

  public GetHierarchy(
    tag1: dcTag,
    validateRules = false,
    fillTemplateName = false,
    includeClassID = false
  ): Observable<dcTreeNode[]> {
    if (tag1) {
      const tempTag = new dcTag();
      tempTag.ObjectID = tag1.ObjectID;
      tempTag.SourceObjectID = tag1.SourceObjectID;
      tempTag.ElementTypeID = tag1.ElementTypeID;
      tempTag.TagName = tag1.TagName;
      tempTag.IsLazyLoading = false;
      tempTag.IsShallowsearch = false;

      if (includeClassID) {
        tempTag.SourceClassID = tag1.SourceClassID;
      }

      return this.httpService.post<dcTreeNode[]>(
        `tags/GetHierarchy?validateRules=${validateRules}&fillTemplateName=${fillTemplateName}`,
        tempTag
      );
    }
  }

  public SaveHierarchyRule(hierarchyRule: dcTreeNode[]): Observable<any> {
    return this.httpService.post<dcActionInfo<any>[]>('tags/SaveHierarchyRule', hierarchyRule);
  }

  public getAllGroupedUOMs(): Observable<NxGroup[]> {
    const apiCall = this.httpService.get<NxGroup[]>('DXManager/GetGroupedUOMs').pipe(
      tap(UOMGroups => {
        const UOMEnumObj = UOM as any as Dictionary<string | number>;
        UOMGroups.forEach(groups => {
          groups.Items.forEach(UOMItem => {
            if (UOMItem.ID && !UOMEnumObj[+UOMItem.ID]) {
              UOMEnumObj[+UOMItem.ID] = UOMItem.Value;
              UOMEnumObj[groups.Value] = +UOMItem.ID;
            }
          });
        });

        return UOMGroups;
      })
    );

    return this.cacheService.get(
      'all-grouped-uoms',
      apiCall,
      40 * 24 * 60 * 60 * 1000 // 40 days
    );
  }

  getAllUOMs() {
    if (!this.uomCache) {
      this.uomCache = this.getAllUOMs2().pipe(shareReplay(CACHE_SIZE));
    }

    return this.uomCache;
  }

  private getAllUOMs2(): Observable<dcUOM[]> {
    return this.httpService.get<dcUOM[]>('tags/GetAllUOMs').pipe(
      map(res => res),
      catchError(this._errorHandler)
    );
  }

  setEventNotifications(latestValue: NotificationMessage<SDKNotifyLog>) {
    this.eventNotifications.next(latestValue);
  }

  MoveAttributeTemplate(
    SourceElementAttributeTemplateMapID: number,
    TagetElementAttributeTemplateMapID: number,
    TargetElementTemplateVersionID: number,
    CreatedBy: number
  ): Observable<any> {
    return this.httpService.get<dcActionInfo<any>[]>(
      // eslint-disable-next-line max-len
      `tags/MoveAttributeTemplate?SourceElementAttributeTemplateMapID=${SourceElementAttributeTemplateMapID}&TagetElementAttributeTemplateMapID=${TagetElementAttributeTemplateMapID}&TargetElementTemplateVersionID=${TargetElementTemplateVersionID}&CreatedBy=${CreatedBy}`
    );
  }

  public SetAttributeValues(attributes: dcAttribute[]): Observable<any> {
    return this.httpService.post<any>('Element/SetAttributeValues', attributes);
  }

  public saveHierarchy(tvp: dcHierarchyTVP[]): Observable<any> {
    return this.httpService.post<any>('tags/SaveHierarchy', tvp);
  }

  public GetRuleForImport(tag: dcTag): Observable<dcTreeNode[]> {
    return this.httpService.post<dcTreeNode[]>('tags/GetRuleForImport', tag);
  }

  // public GetHierarchyPreview(): Observable<any> {
  //   return this.httpService.get<dcTreeNode[]>('tags/GetHierarchyPreview');
  // }

  public GetHierarchyPreview(tag: dcTag): Observable<dcTreeNode[]> {
    return this.httpService.post<dcTreeNode[]>('tags/GetHierarchyPreview', tag);
  }

  // public ImportHierarchy(dcTreeNode: dcTreeNode[]): Observable<any> {
  //   return this.httpService.post<any[]>('tags/ImportHierarchy', dcTreeNode);
  // }
  public ImportHierarchy(): Observable<any> {
    return this.httpService.get<any>('tags/ImportHierarchy');
  }

  public GetHierarchyListOfElement(tag: dcTag): Observable<dcTag[]> {
    return this.httpService.post<dcTag[]>('Element/GetHierarchyListOfElement', tag);
  }

  public GetAllHierarchyOnDemand(tag: dcTreeNode): Observable<{
    m_Item1: dcTreeNode[];
    m_Item2: dcTreeNode[];
  }> {
    return this.httpService.post<{
      m_Item1: dcTreeNode[];
      m_Item2: dcTreeNode[];
    }>('tags/GetAllHierarchyOnDemand', tag);
  }

  // TODO doesn't match the Back-End return type
  public GetElementTemplatesRuleDetails(tag: dcTag) {
    if (tag) {
      const tempTag = new dcTag();
      tempTag.ObjectID = tag.ObjectID;
      tempTag.SourceObjectID = tag.SourceObjectID;
      tempTag.ElementTypeID = tag.ElementTypeID;
      tempTag.TagName = tag.TagName;
      tempTag.IsLazyLoading = false;
      tempTag.IsShallowsearch = false;
      return this.httpService.post<{
        m_Item1: dcElementTemplate[];
        m_Item2: dcTreeNode[];
      }>('tags/GetElementTemplatesRuleDetails', tempTag);
    }
  }

  public convertStringtToDateTime(startTime: string, endTime: string): Observable<TimeRange> {
    return this.httpService
      .get<TimeRange>(`app/convertStringtToDateTime?startTime=${startTime}&endTime=${endTime}`)
      .pipe(
        map(a => {
          console.log(a);
          return a;
        })
      );
  }

  public GetElementHierarchyTree(tag: dcTag): Observable<dcElement[]> {
    return this.httpService.post<dcElement[]>('tags/GetElementHierarchyTree', tag);
  }

  public GetRuleHierarchy(elementTemplateId: number) {
    return this.httpService.get<dcElement[]>(`V1/Entity/GetRuleHierarchy/${elementTemplateId}`);
  }
  // public GetElementTemplateRulesTemplateDetails(tag: dcTag) {
  //   if (tag) {
  //     let tempTag: dcTag = new dcTag();
  //     tempTag.ObjectVersionID = tag.ObjectVersionID;
  //     tempTag.SourceObjectID = tag.SourceObjectID;
  //     tempTag.ElementTypeID = tag.ElementTypeID;
  //     tempTag.TagName = tag.TagName;
  //     tempTag.IsLazyLoading = false;
  //     tempTag.IsShallowsearch = false;
  //     return this.httpService.post<{
  //       m_Item1: dcElement[];
  //       m_Item2: dcElementHeader[];
  //     }>('tags/GetElementTemplateRulesDetails', tempTag);
  //   }
  // }

  public changeElementTemplate(elements: dcElement): Observable<any> {
    return this.httpService.post<dcActionInfo<any>[]>(
      'tags/ChangeTemplateofCreatedInstance',
      elements
    );
  }
  public GetVersiondetails(tag: dcTag): Observable<any> {
    return this.httpService.post<dcElementHeader[]>('tags/GetElementVersionDetails', [tag]);
  }
  public checkIn(element: dcElementHeader): Observable<any> {
    return this.httpService.post<dcActionInfo<any>[]>('tags/CheckIn', element);
  }
  public checkOut(element: dcElementHeader): Observable<any> {
    return this.httpService.post<dcActionInfo<any>[]>('tags/CheckOut', element);
  }

  public getAllAttributeTemplates(ElementTemplateVersionID: number) {
    return this.httpService.post<dcAttributeTemplate[]>(
      'Process/GetAttributeTemplates?ElementTemplateVersionID=' + ElementTemplateVersionID,
      null
    );
  }

  public GetElementsByTags(searhParams: dcSearchTags): Observable<dcElement[]> {
    return this.httpService.post<dcElement[]>('tag/GetElementsByTags', searhParams);
  }

  public GetKPIDetails(tag: dcTag[], caller?: Caller, disableCache?: boolean): Observable<dcKPI[]> {
    const key = this.cacheService.generateCacheKeyForTags(tag);
    if (disableCache) {
      return this.httpService.post<dcKPI[]>('kpi/GetKPIDetails', tag, caller);
    }
    return this.cacheService.get(
      key,
      this.httpService.post<dcKPI[]>('kpi/GetKPIDetails', tag, caller),
      8 * 60 * 60 * 1000 // 8 hours
    );
  }

  public getHierarchyTree(
    selectedElementVersionID: number,
    selectedElementCategoryID: number,
    templateTag: dcTag,
    supportedType: NFIdentity,
    caller?: Caller,
    isRoot?: boolean,
    fetchAll?: boolean
  ): Observable<dcNavigationTree[]> {
    const navigationTag: NavigationTreeSearch = new NavigationTreeSearch();
    navigationTag.SelectedElementVersionID = selectedElementVersionID;
    navigationTag.SelectedElementCategoryID = selectedElementCategoryID;
    if (templateTag) {
      navigationTag.TemplateTags = [templateTag];
    }
    navigationTag.SupportedType = supportedType;
    navigationTag.IsRoot = isRoot;
    navigationTag.FetchAll = fetchAll;

    return this.httpService.post<dcNavigationTree[]>('hierarchy/getTree', navigationTag, caller);
  }

  public deleteElement(elementVersionID: number) {
    return this.httpService.get<any>('tags/DeleteElement?elementVersionID=' + elementVersionID);
  }

  public getElementHeader(tag: dcTag): Observable<dcElementHeader> {
    return this.httpService.post<dcElementHeader>('tags/GetElementHeader', tag);
  }

  public getElementHeaders(tag: dcTag[]): Observable<dcElementHeader[]> {
    return this.httpService.post<dcElementHeader[]>('Element/GetElementHeaders', tag);
  }

  public GetDerivedElementsByTemplateID(
    elementTemplate: dcElementTemplate,
    isDerivedElementsRequired = false
  ): Observable<dcElementHeader[]> {
    return this.httpService.post<dcElementHeader[]>(
      'tags/GetDerivedElementsByTemplateID' +
        (isDerivedElementsRequired ? '?isDerivedElementsRequired=true' : ''),
      elementTemplate
    );
  }

  public GetAppFeedback(appId: number): Observable<Feedback[]> {
    return this.httpService.get<Feedback[]>(`App/GetAppFeedbacks?AppId=${appId}`);
  }

  public viewFile(lstFileDetail: FileAttachmentDetail, download = false) {
    return this.httpService.download<any>(
      'File/DownloadFile' + (download ? '?download=true' : ''),
      lstFileDetail
    );
  }

  public viewFileNew(lstFileDetail: FileAttachmentDetail, download = false) {
    return this.downloadFileForView(lstFileDetail.FileGUID, download);
  }

  public downloadFileForView(fileGUID: string, download = false) {
    return this.httpService.download<any>(
      'File/DownloadAttachment/' + fileGUID + '/' + download,
      null
    );
  }

  public getChildEntityDetails(tag: dcTag, attributeName: string) {
    const fileInfo = new FileSaveInfo();
    fileInfo.tagInfo = tag;
    fileInfo.categoryName = attributeName;
    return this.httpService.post<dcAttribute[]>('File/GetParentChildEntityDetails', fileInfo);
  }

  public saveFileAttributes(attributeInfo: FileSaveInfo) {
    return this.httpService.post<dcActionInfo<any>>('File/SaveFileAttributes', attributeInfo);
  }

  public getRootElementTagInfo(customTags: dcTag[], cancelToken?: Caller) {
    return this.httpService.post<dcTag[]>('File/GetRootElementTagInfo', customTags, cancelToken);
  }

  public getFileMetaData(lstFileDetail: FileAttachmentDetail, download = false) {
    return this.httpService.post<any>(
      'File/GetFileMetaData' + (download ? '?download=true' : ''),
      lstFileDetail
    );
  }

  public saveFiles(formData: FormData) {
    return this.httpService.post<any>('File/saveFiles', formData);
  }

  public saveFilesAdvanced(formData: FormData) {
    return this.httpService.post<FileAttachmentDetail>('File/UploadFile', formData);
  }

  public GetTemplatesByType(elementTypeId: number): Observable<dcElementTemplateHeader[]> {
    return this.cacheService.get(
      'GetTemplatesByType' + elementTypeId,
      this.httpService.get<dcElementTemplateHeader[]>(
        `ElementTemplate/GetTemplatesByType/${elementTypeId}`
      ),
      8 * 60 * 60 * 1000, // 8 hours
      true
    );
  }

  public GetQMByTemplateId(queryTemplateID: number): Observable<dcDataReference[]> {
    return this.httpService.get<dcDataReference[]>(
      `V1/Entity/GetQMByTemplateId/${queryTemplateID}`
    );
  }

  public GetQMHierarchy(queryManagerID: number): Observable<dcTreeNode[]> {
    return this.httpService.get<dcTreeNode[]>(`V1/Entity/GetQMHierarchy/${queryManagerID}`);
  }

  public GetDatareferenceById(
    elementAttributeMapId: number,
    queryManagerId: number,
    isTemplate: boolean
  ): Observable<dcDataReference> {
    return this.httpService.get<dcDataReference>(
      'V1/Entity/GetDatareferenceById?elementAttributeMapId=' +
        elementAttributeMapId +
        '&queryManagerId=' +
        queryManagerId +
        '&isTemplate=' +
        isTemplate
    );
  }

  public GetDatareference(
    elementAttributeMapId: number,
    queryManagerId: number,
    isTemplate: boolean
  ) {
    return this.httpService.get<dcDataReference>(
      'V1/Entity/GetDatareferenceById?elementAttributeMapId=' +
        elementAttributeMapId +
        '&queryManagerId=' +
        queryManagerId +
        '&isTemplate=' +
        isTemplate
    );
  }

  public saveRule(flatData: dcElement[], elements: dcElement[]) {
    const invalidList: string[] = [];
    flatData.map(element => {
      if (
        element.Header.ElementTemplateID == AppConstants.ElementTemplateVersionIds.HIERARCHY_RULE
      ) {
        const namingProperty = element.Attributes.find(
          attribute => attribute.Header.AttributeTemplateName == 'NAMING_PATTERN'
        );
        if (
          !namingProperty ||
          !namingProperty.Header.ValueString ||
          !namingProperty.Header.ValueString.trim()
        ) {
          invalidList.push(element.Header.ElementName);
        }
      }
    });

    if (invalidList && invalidList.length > 0) {
      this.toastService.openWarningToast(invalidList.join(', '), 'Invalid Naming Pattern For');
    } else {
      if (elements.length > 0) {
        this.createElements(elements).subscribe({
          next: res => this.toastService.openSuccessToast(res.StatusMessage),
          error: () => this.toastService.openErrorToast('Error occured')
        });
      } else {
        this.toastService.openErrorToast('Please check rule.');
      }
    }
  }

  public getPortDetails(): Observable<any> {
    return this.httpService.get<any>(`ElementTemplate/PortTypes`);
  }

  public getPortDirections(): Observable<any> {
    return this.httpService.get<any>('ElementTemplate/PortDirections');
  }

  public getAllPortTemplates(): Observable<dcPortTemplateValue[]> {
    return this.httpService.get<dcPortTemplateValue[]>(`ElementTemplate/GetAllPortTemplates`);
  }

  public getPortTemplateDetails(portTemplateId: any): Observable<any> {
    return this.httpService.get<any>(
      `ElementTemplate/GetPortTemplateDetails?portTemplateId=${portTemplateId}`
    );
  }

  public savePorts(newPort: PortTemplates[]): Observable<PortTemplates> {
    return this.httpService.post<PortTemplates>(`ElementTemplate/SavePortTemplateMap`, newPort);
  }

  public savePortConnection(data: PortConnections[]): Observable<PortConnections> {
    return this.httpService.post<PortConnections>(`Element/Ports`, data);
  }

  public movePortTemplate(data: any): Observable<PortTemplates> {
    return this.httpService.post<PortTemplates>(`ElementTemplate/MovePortTemplate`, data);
  }
  public deletePortConnection(data: PortConnections[]): Observable<PortConnections> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: data
    };
    return this.http.delete<PortConnections>(
      this.commonService.constructUrl(`Element/Ports`),
      options
    );
  }

  /**
   * Retrieves port element templates based on the provided element type ID.
   * Sends a GET request to the backend API endpoint and returns an observable
   * of dcTag objects associated with the specified element type.
   *
   * @param elementTypeID - The ID of the element type to retrieve templates for.
   * @returns An Observable emitting an array of dcTag objects.
   */
  public getPortElementTemplatesByElementType(elementTypeID: number): Observable<dcTag[]> {
    return this.httpService.get<dcTag[]>(
      `ElementTemplate/GetPortElementTemplatesByElementType?elementTypeID=${elementTypeID}`
    );
  }

  public getAttributeValueForSelectedQuery(
    rootElementVersionID: number,
    elementPath: string,
    queryID: number
  ): Observable<SmartDataRequestParam[]> {
    return this.httpService.post<SmartDataRequestParam[]>(
      // eslint-disable-next-line max-len
      `Element/GetAttributeValueForSelectedQuery?RootElementVersionID=${rootElementVersionID}&ElementPath=${elementPath}&QueryID=${queryID}`,
      null
    );
  }

  public getAllQueryForSelectedRootElement(
    rootElementVersionID: number,
    elementPath: string
  ): Observable<dcElementHeader[]> {
    return this.httpService.post<dcElementHeader[]>(
      `Element/GetAllQueryForSelectedRootElement?RootElementVersionID=${rootElementVersionID}&ElementPath=${elementPath}`,
      null
    );
  }

  public getPortDependencies(
    ElementPortTemplateMapId: any,
    caller?: Caller
  ): Observable<PortTemplates> {
    return this.httpService.get<PortTemplates>(
      `ElementTemplate/DeletePortTemplateMap?ElementPortTemplateMapID=${ElementPortTemplateMapId}`,
      null,
      caller
    );
  }

  public deletePort(ElementPortTemplateMapId: any): Observable<PortTemplates> {
    return this.httpService.delete<PortTemplates>(
      `ElementTemplate/DeletePortTemplateMap?ElementPortTemplateMapID=${ElementPortTemplateMapId}`
    );
  }

  public updatePorts(newPort: PortTemplates[]): Observable<PortTemplates> {
    return this.httpService.put<PortTemplates>(`ElementTemplate/SavePortTemplateMap`, newPort);
  }

  public getAppList(data: dcConnectedAppsFilter): Observable<dcActionInfo<dcTag>> {
    return this.httpService.post<dcActionInfo<dcTag>>(`tags/GetAppsForConnectedElement`, data);
  }

  public GetPossibleChildElementTemplatesByRule(elementTemplateID: number) {
    if (elementTemplateID && elementTemplateID > 0) {
      return this.httpService.get<{
        m_Item1: dcElementTemplate[];
        m_Item2: dcTreeNode[];
      }>(`templates/GetPossibleChildElementTemplatesByRule/${elementTemplateID}`);
    }
  }

  public RenameElement(
    isRename: boolean,
    elementName: string,
    rootRuleElementVersionID?: number,
    ruleElementVersionID?: number,
    elementVersionID?: number
  ) {
    return this.httpService.post<dcAttributeHeader[]>(
      `Element/RenameElementFromRule?PatternOrElementName=${elementName}
      &RootRuleElementVersionID=${rootRuleElementVersionID}
      &RuleElementVersionID=${ruleElementVersionID}
      &ElementVersionID=${elementVersionID}
      &IsRename=${isRename}`,
      null
    );
  }

  public ElementDelete(elementVersionID: number, isDelete: boolean) {
    if (elementVersionID && elementVersionID > 0) {
      return this.httpService.delete(`Element/${elementVersionID}?IsDelete=${isDelete}`);
    }
  }

  public DeleteAttributeTemplate(elementAttributeTemplateMapID: number, isDelete: boolean) {
    if (elementAttributeTemplateMapID && elementAttributeTemplateMapID > 0) {
      return this.httpService.delete(
        `ElementTemplate/AttributeTemplate/${elementAttributeTemplateMapID}?IsDelete=${isDelete}`
      );
    }
  }

  public getElementTemplatesByElementId(elementVersionID: number) {
    if (elementVersionID) {
      const templateList = this.elementTemplateCache.get(elementVersionID) || [];
      if (templateList && templateList.length) {
        return of(templateList);
      } else {
        return this.httpService
          .get<dcElementTemplateHeader[]>(
            `Element/GetParentTemplatesByElementId?elementVersionID=${elementVersionID}`
          )
          .pipe(
            tap(result => {
              this.elementTemplateCache.set(elementVersionID, result);
            })
          );
      }
    }
  }

  public processRequestWithResponse(data: DataRequest[]): Observable<DataRequest[]> {
    return this.httpService.post<DataRequest[]>(`tags/ProcessRequestWithResponse`, data);
  }

  public subscribeToNotifications(
    notifications: KeyValuePair<LoggingDetails>
  ): Observable<boolean> {
    return this.httpService.post<boolean>('Log/SubscribeToLogs', notifications.getItems());
  }

  public enableProfiler(isProfilerEnabled: boolean): Observable<boolean> {
    return this.httpService.post<boolean>(`Log/EnableProfiler/${isProfilerEnabled}`, null);
  }

  public deleteFile(files: FileAttachmentDetail[]): Observable<dcActionInfo<string>> {
    return this.httpService.post<dcActionInfo<string>>('File/DeleteFile', files);
  }

  public getDerivedTemplatesTreeNodes(
    cancelToken: string,
    elementTemplateId: number,
    levelDepth?: number
  ): Observable<dcTreeNode[]> {
    return this.cacheService.get(
      `getDerivedTemplatesTreeNodes${elementTemplateId}_${levelDepth}`,
      this.httpService.get<dcTreeNode[]>(
        `ElementTemplate/DerivedTemplatesTreeNodes/${elementTemplateId}/${levelDepth || ''}`,
        null,
        cancelToken
      ),
      8 * 60 * 60 * 1000 // 8 hours
    );
  }

  getVersionDetails(): Observable<ServiceInfo> {
    return this.httpService.get<ServiceInfo>(`Service/Info`);
  }

  resolveSubstitutionParams(
    attribute: dcSubstitutionParameter
  ): Observable<dcQueryParameterHeaderExtn[]> {
    return this.httpService.post('DXManager/ResolveSubstitutionParams', attribute);
  }

  parameterizeText(
    rootElementVersionID: number,
    inputText: string,
    navigationHierarchyCategoryID: number = -1
  ) {
    return this.httpService.get<dcParameterizationOutput>(
      `DXManager/ParameterizeText/${rootElementVersionID}/${navigationHierarchyCategoryID}/?InputText=${inputText}`
    );
  }

  hasParameters(text: string) {
    return text.includes('{') || text.includes('}');
  }

  async getUOM({ id, name }: { id?: number; name?: string }) {
    const uomList = await firstValueFrom(this.getAllUOMs()).catch(e => [] as dcUOM[]);
    return uomList.find(uom => uom.UOMID === id || uom.Name === name);
  }

  public callRestApi(url: string) {
    return this.httpService.get<dcActionInfoSingle<Record<string, GenericValue>>>(url);
  }

  public addUpdateSuggestedValues(suggestedValue: dcSuggestedValue) {
    return this.httpService.post<dcActionInfo<dcSuggestedValue>>(
      'app/AddOrUpdateSuggestedValues',
      suggestedValue
    );
  }
}
